import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  TextField,
  Typography
} from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Loading from 'src/components/Loading';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { selectAvalBuroFormId } from 'src/store/selectors/avalBuroFormSelectors';
import {
  createAvalBuroFormAction,
  resendAvalNipCode,
  validateAvalNip,
  CREATE_AVAL_BURO_FORM,
  VALIDATE_AVAL_NIP
} from 'src/store/actions/avalBuroFormActions';
import { resendNipSeconds } from 'src/utils/constants/buroForm';
import { formatCountdown } from 'src/utils/common';
import transformAvalFormData from 'src/utils/constants/AvalBuroForm';
import theme from 'src/theme';
import useStyles from '../styles';

const StepThree = ({ data, onNext, onBack }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formId = useSelector(selectAvalBuroFormId);
  const isCreating = useSelector(isLoadingSelector([CREATE_AVAL_BURO_FORM.action]));
  const isValidating = useSelector(isLoadingSelector([VALIDATE_AVAL_NIP.action]));
  const [nip, setNip] = useState('');
  const [countdown, setCountdown] = useState(resendNipSeconds);

  useEffect(() => {
    if (!formId) {
      dispatch(createAvalBuroFormAction(transformAvalFormData(data)));
    }
  }, []);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResend = useCallback(() => {
    dispatch(resendAvalNipCode());
    setCountdown(resendNipSeconds);
  }, [dispatch]);

  const handleVerify = useCallback(() => {
    dispatch(validateAvalNip(nip, onNext));
  }, [dispatch, nip, onNext]);

  if (isCreating) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
        <Loading />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('AVAL_BURO_FORM.STEP_THREE.PREPARING')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box>
        <Typography
          variant="h3"
          sx={{ marginBottom: '10px', color: theme.palette.primary.main }}
        >
          {t('AVAL_BURO_FORM.STEP_THREE.TITLE')}
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: '20px' }}>
          {t('AVAL_BURO_FORM.STEP_THREE.DESCRIPTION')}
        </Typography>

        <TextField
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: 6,
            autoComplete: 'off',
            'aria-describedby': 'nip-resend-hint'
          }}
          fullWidth
          label="NIP"
          margin="normal"
          name="nip"
          onChange={e => setNip(e.target.value)}
          type="text"
          value={nip}
          variant="outlined"
        />
        <Box
          id="nip-resend-hint"
          sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}
        >
          <Button
            variant="text"
            onClick={handleResend}
            disabled={countdown > 0}
            color="primary"
          >
            {countdown > 0
              ? `${t('AVAL_BURO_FORM.STEP_THREE.RESEND_NIP_IN')} ${formatCountdown(countdown)}`
              : t('AVAL_BURO_FORM.STEP_THREE.RESEND_NIP')}
          </Button>
        </Box>
      </Box>

      <Box className={classes.box}>
        <Button
          className={classes.buttons}
          onClick={onBack}
          color="primary"
          variant="outlined"
        >
          {t('COMMON.BACK')}
        </Button>
        <Button
          className={clsx(classes.buttons, classes.buttonMain)}
          onClick={handleVerify}
          disabled={!nip || isValidating}
          color="primary"
          variant="contained"
        >
          {t('AVAL_BURO_FORM.STEP_THREE.VERIFY')}
        </Button>
      </Box>
    </Box>
  );
};

StepThree.propTypes = {
  data: PropTypes.object,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired
};

export default StepThree;
